import type { Shop } from "@/lib/api";
import { resolveShopLogoUrl, shopInitials } from "@/lib/shop-logos";
import { cn } from "@/lib/utils";

type ShopLogoSize = "xs" | "sm" | "md" | "lg";

const sizeClasses: Record<ShopLogoSize, string> = {
  xs: "h-5 w-5 rounded",
  sm: "h-8 w-8 rounded-md",
  md: "h-10 w-10 rounded-md",
  lg: "h-12 w-12 rounded-xl",
};

const textSizeClasses: Record<ShopLogoSize, string> = {
  xs: "text-[10px]",
  sm: "text-xs",
  md: "text-sm",
  lg: "text-sm",
};

export function ShopLogo({
  shop,
  name,
  slug,
  logoUrl,
  size = "md",
  className,
}: {
  shop?: Pick<Shop, "name" | "slug" | "logoUrl"> | null;
  name?: string;
  slug?: string | null;
  logoUrl?: string | null;
  size?: ShopLogoSize;
  className?: string;
}) {
  const label = name ?? shop?.name ?? "Shop";
  const resolvedLogo = resolveShopLogoUrl(shop ?? { slug, logoUrl });
  const plateClassName = cn(
    "grid shrink-0 place-items-center overflow-hidden border border-border/60 bg-background/80 ring-1 ring-foreground/5",
    sizeClasses[size],
    className,
  );

  if (resolvedLogo) {
    return (
      <span className={plateClassName}>
        <img
          src={resolvedLogo}
          alt={label}
          className="h-full w-full object-contain p-1.5"
          loading="lazy"
        />
      </span>
    );
  }

  return (
    <span
      className={cn(
        plateClassName,
        "bg-primary/15 font-display font-black text-primary ring-primary/20",
        textSizeClasses[size],
      )}
      aria-label={label}
    >
      {shopInitials(label)}
    </span>
  );
}